<template>
  <ClientOnly>
    <div
      class="bubble-wrapper"
      :class="{ 'is-hidden': shouldHideInMobile, [bubbleWrapperPositionClass]: true }"
      :style="cssVariables"
      @mouseenter="handleFirstHover"
    >
      <!-- Message fixed above buttons -->
      <div
        v-if="isBubbleMessageShown && !isDrawerOpen && props.clientInfo.chatConfiguration.branding.bubble_message_enabled"
        class="bubble-message-container"
      >
        <span v-html="compiledBubbleMessage" />
        <button
          class="close-button"
          :aria-label="$t('chatAndContact.drawer.chatBubble.closeBubbleMessage')"
          @click="isBubbleMessageShown = false"
        >
          <Icon
            name="lucide:x"
            size="18"
          />
        </button>
      </div>

      <!-- Buttons container -->
      <div
        class="bubble-container"
      >
        <!-- Inbox/Feedback Button -->
        <button
          class="bubble-button inbox-button"
          :class="{ 'is-active': activeState === 'inbox', [bubbleButtonSizeClass]: true }"
          :aria-label="activeState === 'inbox' ? $t('chatAndContact.drawer.chatBubble.closeInbox') : $t('chatAndContact.drawer.chatBubble.openInbox')"
          :aria-pressed="activeState === 'inbox'"
          @click="handleInboxClick"
        >
          <Icon name="lucide:mail" />
        </button>

        <!-- AI Chat Button -->
        <button
          class="bubble-button ai-button"
          :class="{ 'is-active': activeState === 'ai-chat', [bubbleButtonSizeClass]: true }"
          :aria-label="activeState === 'ai-chat' ? $t('chatAndContact.drawer.chatBubble.closeAiChat') : $t('chatAndContact.drawer.chatBubble.openAiChat')"
          :aria-pressed="activeState === 'ai-chat'"
          @click="handleAiChatClick"
        >
          <Icon name="lucide:sparkles" />
        </button>
      </div>
    </div>
  </ClientOnly>
</template>

<script setup>
import { useBreakpoints, breakpointsTailwind } from "@vueuse/core";
import { computed, ref, toRef, useNuxtApp, watchEffect } from "#imports";
import { useDebugLogger, useChatTheme, useMarkdownCompiler } from "../../composables";
const props = defineProps({
  activeState: { type: String, required: false, default: "closed" },
  clientInfo: { type: Object, required: true }
});
const { $t } = useNuxtApp();
const emit = defineEmits(["stateChange", "firstHover"]);
const { log } = useDebugLogger();
const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("md");
const { cssVariables } = useChatTheme(toRef(props, "clientInfo"));
const hasHovered = ref(false);
const isBubbleMessageShown = ref(true);
const { compileMarkdown } = useMarkdownCompiler();
const compiledBubbleMessage = ref("");
const isDrawerOpen = computed(() => {
  return props.activeState !== "closed";
});
const shouldHideInMobile = computed(() => {
  return isMobile.value && isDrawerOpen.value;
});
const bubbleButtonSizeClass = computed(() => {
  switch (props.clientInfo.chatConfiguration.branding.icon_size) {
    case "large":
      return "large-size";
    case "medium":
      return "medium-size";
    default:
      return "small-size";
  }
});
const bubbleWrapperPositionClass = computed(() => {
  return props.clientInfo.chatConfiguration.branding.icon_position === "left" ? "left-bubble-wrapper" : "right-bubble-wrapper";
});
watchEffect(() => {
  compileMarkdown(props.clientInfo.chatConfiguration.branding.bubble_message).then((result) => {
    compiledBubbleMessage.value = result;
  }).catch((error) => {
    console.error("Error compiling markdown:", error);
    compiledBubbleMessage.value = $t("chatAndContact.drawer.chatBubble.defaultBubbleMessage");
  });
});
function handleFirstHover() {
  if (!hasHovered.value) {
    hasHovered.value = true;
    emit("firstHover");
    log("\u{1F3AF} First hover detected - loading drawer components");
  }
}
function handleInboxClick() {
  const newState = props.activeState === "inbox" ? "closed" : "inbox";
  emit("stateChange", newState);
}
function handleAiChatClick() {
  const newState = props.activeState === "ai-chat" ? "closed" : "ai-chat";
  emit("stateChange", newState);
}
</script>

<style scoped>
.bubble-wrapper{--active-text-color:#fff;--bubble-bg:hsla(0,0%,100%,.95);align-items:flex-end;bottom:32px;display:flex;flex-direction:column;gap:16px;position:fixed;transition:opacity .3s cubic-bezier(.4,0,.2,1),visibility .3s cubic-bezier(.4,0,.2,1);z-index:999}.bubble-wrapper.left-bubble-wrapper{align-items:flex-start;left:72px}.bubble-wrapper.right-bubble-wrapper{right:72px}.bubble-wrapper.is-hidden{opacity:0;pointer-events:none;visibility:hidden}.bubble-message-container{align-items:flex-start;background:var(--ce-chat-color-blue-50);border:1px solid var(--ce-chat-color-primary);border-radius:var(--ce-chat-color-border-radius-md) var(--ce-chat-color-border-radius-md) var(--ce-chat-color-border-radius-none) var(--ce-chat-color-border-radius-md);color:var(--ce-chat-color-grey-950);display:flex;font-family:Inter;font-weight:400;gap:24px;justify-content:center;line-height:22px;max-height:123px;max-width:339px;overflow:hidden;padding:8px 9px 8px 12px;word-break:break-word}.bubble-message-container :deep(p){margin:0}.bubble-wrapper.left-bubble-wrapper .bubble-message-container{border-radius:var(--border-radius-md) var(--border-radius-md) var(--border-radius-md) var(--border-radius-none)}.bubble-message-container .close-button{background:transparent;border:none;cursor:pointer}.bubble-container{-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px);border:1px solid var(--ce-chat-color-gray-200);border-radius:40px;gap:8px;padding:4px}.bubble-button,.bubble-container{background:var(--bubble-bg);display:flex}.bubble-button{align-items:center;border:none;border-radius:32px;color:var(--ce-chat-color-primary);cursor:pointer;font-size:16px;justify-content:center;padding:12px;transition:all .3s cubic-bezier(.4,0,.2,1)}.bubble-button.small-size{height:48px;width:48px}.bubble-button.small-size span.iconify{height:24px;width:24px}.bubble-button.medium-size{height:56px;width:56px}.bubble-button.medium-size span.iconify{height:32px;width:32px}.bubble-button.large-size{height:64px;width:64px}.bubble-button.large-size span.iconify{height:32px;width:32px}.bubble-button:hover{background:color-mix(in srgb,var(--ce-chat-color-primary) 10%,#fff);transform:translateY(-1px)}.bubble-button:active{color:var(--active-text-color);transform:translateY(0)}.bubble-button:focus{outline:none}.bubble-button.is-active{background:var(--ce-chat-color-ai-gradient);color:var(--active-text-color);transform:translateY(0)}@media (max-width:768px){.bubble-wrapper{bottom:24px;right:24px}}
</style>
